import { makeAutoObservable, runInAction } from "mobx";
import { coreapi } from "services/coreapi";
import { RealtimeService, type RealtimeServiceEvent } from "services/realtime";
import { fromTemplate, type Companion } from "src/models/companion";
import {
  platformapi,
  platformapiClient,
  toMillis,
  type ChatMessageDTO,
  type ChatSummary,
} from "src/services/platformapi";

/** A message as the chat screen shows it. */
export type ChatMessage = {
  id: string;
  chatId: string;
  from: "user" | "companion";
  text: string;
  /** Epoch ms. */
  sentAt: number;
  /** Sent by this device, not yet confirmed by the server. */
  pending?: boolean;
  /** The send failed: offer a retry. */
  failed?: boolean;
  /** Moderation blocked it; no reply will come. */
  blocked?: boolean;
};

type LoadState = "loading" | "ready" | "error";

function toMessage(dto: ChatMessageDTO): ChatMessage {
  return {
    id: dto.id,
    chatId: dto.chatId,
    from: dto.senderKind === "USER" ? "user" : "companion",
    text: dto.content,
    sentAt: toMillis(dto.createdAt),
    blocked: dto.isFlagged && dto.flagAction === "suppressed",
  };
}

/** Local calendar day, "2026-10-19". */
function dayKey(at: number): string {
  const d = new Date(at);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/**
 * Companions, chats and messages, from lovelica-core. Replies and "typing"
 * arrive over the realtime socket; after any gap the socket triggers an
 * HTTP resync. One per signed-in user (see ChatStoreProvider).
 *
 * The progress numbers (`messagesSent`, `streakDays`) are display-only and
 * counted on this device (see models/progress.ts).
 */
export class ChatStore {
  companions: Companion[] = [];
  catalogueState: LoadState = "loading";

  /** Template id → the user's bot (Avatar) id. */
  private botIdByTemplate = new Map<string, string>();
  /** Template id → chat summary. */
  chats = new Map<string, ChatSummary>();
  /** Chat id → messages, oldest first. */
  private threads = new Map<string, ChatMessage[]>();
  /** Chat ids whose companion is typing. */
  private typing = new Set<string>();
  /** Template ids whose chat is being opened. */
  opening = new Set<string>();

  private sentDays: string[] = [];
  private sentCount = 0;

  readonly realtime: RealtimeService;

  constructor(
    private userID: string,
    onUnauthorized: () => Promise<boolean>,
  ) {
    makeAutoObservable<this, "userID" | "realtime" | "botIdByTemplate">(this, {
      userID: false,
      realtime: false,
      botIdByTemplate: false,
    });
    this.loadLocalStats();
    this.realtime = new RealtimeService({
      client: platformapiClient,
      getTicket: coreapi.getChatTicket,
      onUnauthorized,
      onHttpResync: this.resync,
    });
  }

  /* ------------------------------- lifecycle ------------------------------ */

  init = async (): Promise<void> => {
    this.realtime.onEvent(this.handleFrame);
    this.realtime.connect();
    await this.loadAll();
  };

  dispose = (): void => {
    this.realtime.offEvent(this.handleFrame);
    this.realtime.disconnect();
  };

  loadAll = async (): Promise<void> => {
    runInAction(() => (this.catalogueState = "loading"));
    try {
      const [templates, bots, chats] = await Promise.all([
        platformapi.listTemplates(),
        platformapi.listUserBots(),
        platformapi.listChats(),
      ]);
      runInAction(() => {
        this.companions = templates.flatMap((t) => fromTemplate(t) ?? []);
        this.botIdByTemplate = new Map(bots.map((b) => [b.templateId, b.id]));
        this.chats = new Map(chats.map((c) => [c.bot.templateId, c]));
        this.catalogueState = "ready";
      });
    } catch (err) {
      console.error("Failed to load companions:", err);
      runInAction(() => (this.catalogueState = "error"));
    }
  };

  /** After a socket gap: refresh the chat list and every loaded thread. */
  private resync = async (): Promise<void> => {
    try {
      const chats = await platformapi.listChats();
      runInAction(() => {
        this.chats = new Map(chats.map((c) => [c.bot.templateId, c]));
      });
      await Promise.all([...this.threads.keys()].map(this.loadThread));
    } catch (err) {
      console.error("Resync failed:", err);
    }
  };

  /* -------------------------------- reading ------------------------------- */

  findCompanion(id: string | undefined): Companion | undefined {
    return this.companions.find((c) => c.id === id);
  }

  private chatIdFor(companionId: string): string | undefined {
    return this.chats.get(companionId)?.id;
  }

  thread(companionId: string): readonly ChatMessage[] {
    const chatId = this.chatIdFor(companionId);
    return (chatId && this.threads.get(chatId)) || [];
  }

  hasChat(companionId: string): boolean {
    return this.chats.has(companionId);
  }

  isTyping(companionId: string): boolean {
    const chatId = this.chatIdFor(companionId);
    return chatId ? this.typing.has(chatId) : false;
  }

  isOpening(companionId: string): boolean {
    return this.opening.has(companionId);
  }

  /** Chats with a last message, newest first. */
  get recentChats(): { companion: Companion; last: ChatMessage }[] {
    return this.companions
      .flatMap((companion) => {
        const chat = this.chats.get(companion.id);
        const loaded = chat && this.threads.get(chat.id);
        const last = loaded?.length
          ? loaded[loaded.length - 1]
          : chat?.lastMessage
            ? toMessage(chat.lastMessage)
            : null;
        return last ? [{ companion, last }] : [];
      })
      .sort((a, b) => b.last.sentAt - a.last.sentAt);
  }

  /** Companions the user has a chat with. */
  get companionsMet(): number {
    return this.companions.filter((c) => this.chats.has(c.id)).length;
  }

  get messagesSent(): number {
    return this.sentCount;
  }

  /** Days in a row, ending today or yesterday, with a message sent here. */
  get streakDays(): number {
    const days = new Set(this.sentDays);
    const day = new Date();
    if (!days.has(dayKey(day.getTime()))) day.setDate(day.getDate() - 1);
    let streak = 0;
    while (days.has(dayKey(day.getTime()))) {
      streak++;
      day.setDate(day.getDate() - 1);
    }
    return streak;
  }

  /* ------------------------------- actions -------------------------------- */

  /**
   * Make sure the user's bot and the chat exist, then load the messages.
   * Safe to call on every visit: both server calls are idempotent.
   */
  openChat = async (companion: Companion): Promise<void> => {
    if (this.opening.has(companion.id)) return;
    this.opening.add(companion.id);
    try {
      let botId = this.botIdByTemplate.get(companion.id);
      if (!botId) {
        const bot = await platformapi.createUserBot(companion.id);
        botId = bot.id;
        this.botIdByTemplate.set(companion.id, botId);
      }
      const chat = await platformapi.startChat(botId);
      runInAction(() => this.chats.set(companion.id, chat));
      await this.loadThread(chat.id);
      const statuses = await platformapi.getStatuses(chat.id);
      runInAction(() => this.setTyping(chat.id, statuses));
    } finally {
      runInAction(() => this.opening.delete(companion.id));
    }
  };

  private loadThread = async (chatId: string): Promise<void> => {
    const dtos = await platformapi.getMessages(chatId);
    runInAction(() => {
      const server = dtos.map(toMessage).reverse();
      // Keep this device's unconfirmed sends at the end.
      const local = (this.threads.get(chatId) ?? []).filter(
        (m) => (m.pending || m.failed) && !server.some((s) => s.id === m.id),
      );
      this.threads.set(chatId, [...server, ...local]);
    });
  };

  send = async (companion: Companion, text: string): Promise<void> => {
    const chatId = this.chatIdFor(companion.id);
    const content = text.trim();
    if (!chatId || !content) return;

    const message: ChatMessage = {
      id: crypto.randomUUID(),
      chatId,
      from: "user",
      text: content,
      sentAt: Date.now(),
      pending: true,
    };
    this.upsert(message);
    this.countSent(message.sentAt);
    await this.deliver(message);
  };

  retry = async (message: ChatMessage): Promise<void> => {
    this.upsert({ ...message, failed: false, pending: true });
    await this.deliver(message);
  };

  private deliver = async (message: ChatMessage): Promise<void> => {
    try {
      const dto = await platformapi.sendMessage({
        id: message.id,
        chatId: message.chatId,
        content: message.text,
      });
      runInAction(() => this.upsert(toMessage(dto)));
    } catch (err) {
      console.error("Send failed:", err);
      runInAction(() =>
        this.upsert({ ...message, pending: false, failed: true }),
      );
    }
  };

  /** Insert or replace by id, keeping the thread in time order. */
  private upsert(message: ChatMessage): void {
    const thread = this.threads.get(message.chatId) ?? [];
    const next = thread.filter((m) => m.id !== message.id);
    next.push(message);
    next.sort((a, b) => a.sentAt - b.sentAt);
    this.threads.set(message.chatId, next);
  }

  private setTyping(
    chatId: string,
    statuses: { kind: string; actorKind: string }[],
  ): void {
    const typing = statuses.some(
      (s) => s.kind === "TYPING" && s.actorKind !== "USER",
    );
    if (typing) this.typing.add(chatId);
    else this.typing.delete(chatId);
  }

  private handleFrame = (frame: RealtimeServiceEvent): void => {
    switch (frame.type) {
      case "chat.message.created":
      case "chat.message.updated": {
        const message = toMessage(frame.payload.message);
        // Only chats this app knows (safe companions).
        if ([...this.chats.values()].some((c) => c.id === message.chatId)) {
          this.upsert(message);
          if (message.from === "companion") this.typing.delete(message.chatId);
        }
        break;
      }
      case "chat.status.updated":
        this.setTyping(frame.payload.chatId, frame.payload.statuses);
        break;
      default:
        break;
    }
  };

  /* ----------------------- display-only local stats ----------------------- */

  private get statsKey(): string {
    return `truelovelica:stats:v1:${this.userID}`;
  }

  private loadLocalStats(): void {
    try {
      const raw = JSON.parse(localStorage.getItem(this.statsKey) ?? "{}");
      this.sentCount = typeof raw.sent === "number" ? raw.sent : 0;
      this.sentDays = Array.isArray(raw.days) ? raw.days : [];
    } catch {
      // Unreadable: start from zero.
    }
  }

  private countSent(at: number): void {
    this.sentCount++;
    const day = dayKey(at);
    if (!this.sentDays.includes(day))
      this.sentDays = [...this.sentDays, day].slice(-60);
    try {
      localStorage.setItem(
        this.statsKey,
        JSON.stringify({ sent: this.sentCount, days: this.sentDays }),
      );
    } catch {
      // Storage blocked: the numbers just don't survive a reload.
    }
  }
}
